import {Component, OnInit, inject, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import Swal from "sweetalert2";
import {DataService} from "../../../services/data.service";
import {ProjectStatus, ProjectTab, Right} from "../../interfaces";
import {AccessService} from "../../../services/access.service";
import {ProjectService} from "../../../services/project.service";
import {requiredConfig} from "../../project/required.config";
import {ProjectNamingPipe} from "../../../pipes/project-naming.pipe";
import {ToastrService} from "ngx-toastr";
import {NgIf, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {ResearchIdeaComponent} from './research-idea/research-idea.component';
import {ResearchGoalsComponent} from './research-goals/research-goals.component';
import {ResearchOrganisationComponent} from './research-organisation/research-organisation.component';
import {ResearchCostsComponent} from './research-costs/research-costs.component';
import {ResearchDatesComponent} from './research-dates/research-dates.component';
import {ResearchReportingComponent} from './research-reporting/research-reporting.component';
import {ResearchRisksComponent} from './research-risks/research-risks.component';
import {ResearchStakeholderComponent} from './research-stakeholder/research-stakeholder.component';
import {ResearchExternalComponent} from './research-external/research-external.component';
import {ResearchApprovalsComponent} from './research-approvals/research-approvals.component';
import {ResearchDocumentsComponent} from './research-documents/research-documents.component';
import {ResearchCompletionComponent} from './research-completion/research-completion.component';
import {ProjectStatusPipe} from '../../../pipes/project-status.pipe';

@Component({
    selector: 'app-research-detail',
    templateUrl: './research-detail.component.html',
    styleUrl: './research-detail.component.scss',
    imports: [
        NgIf, RouterLink, TooltipModule, ResearchIdeaComponent, ResearchGoalsComponent, ResearchOrganisationComponent, ResearchCostsComponent, ResearchDatesComponent,
        ResearchReportingComponent, ResearchRisksComponent, ResearchStakeholderComponent, ResearchExternalComponent, ResearchApprovalsComponent, ResearchDocumentsComponent,
        ResearchCompletionComponent, DatePipe, ProjectStatusPipe, ProjectNamingPipe
    ]
})
export class ResearchDetailComponent implements OnInit {

    accessService = inject(AccessService);
    projectService = inject(ProjectService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private dataService = inject(DataService);
    private projectNamingPipe = inject(ProjectNamingPipe);
    private toastrService = inject(ToastrService);

    private readonly requiredConfig = requiredConfig;
    readonly ProjectTab = ProjectTab;
    readonly ProjectStatus = ProjectStatus;
    readonly Right = Right;

    badges = {
        goals: [],
        organisation: [],
        costs: [],
        dates: [],
        completion: [],
        external: [],
    };

    tab: ProjectTab = ProjectTab.IDEA;

    PID: string;
    project: any;

    // Set to true when access to the project is denied as per user settings
    accessDenied = false;

    // Set to true when project does not exist
    missing = false;

    // Disabled when a subcomponent is an edit mode
    navigationDisabled = false;

    // Used to inform children to reload
    reloadCounter = signal(0);

    filedCompletion = false;

    exporting = false;

    ngOnInit(): void {
        this.PID = this.route.snapshot.paramMap.get('id');

        if (this.PID !== '0') {
            this.loadProject();
        }
    }

    // executed when called from children
    handleChangeProject(): void {
        this.loadProject();
    }

    reloadChildren(): void {
        this.reloadCounter.update(v => v + 1);
    }

    loadProject(): void {
        this.dataService.request("Research/get", {
            PID: this.PID
        }).subscribe(response => {
            this.project = response.project;

            this.checkBadges();
        })
    }

    checkBadges(): void {
        this.badges = {
            goals: [],
            organisation: [],
            costs: [],
            dates: [],
            completion: [],
            external: [],
        };

        this.requiredConfig.forEach(config => {
            if (config.required.includes(this.project.status)) {
                if (Array.isArray(this.project[config.field]) && this.project[config.field].length == 0) {
                    this.badges[config.tab].push(config.name);
                } else {
                    if (!this.project[config.field] || this.project[config.field] == '0') {
                        this.badges[config.tab].push(config.name);
                    }
                }
            }
        })
    }

    setTab(tab: ProjectTab): void {
        if (this.navigationDisabled) return;

        this.tab = tab;
    }

    gotoList(): void {
        this.router.navigate(['/research']);
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: this.projectNamingPipe.transform(this.project.status) + ' wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Research/delete', {PID: this.PID}).subscribe(() => {
                    this.router.navigate(['/research']);
                });
            }
        });
    }

    toggleNavigation(value: boolean): void {
        this.navigationDisabled = value;
    }

    resetStatus(): void {
        Swal.fire({
            title: 'Sicher?',
            text: "Sicher, dass Sie den Status On Hold aufheben wollen?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Research/resetStatus', {
                    PID: this.PID,
                }).subscribe(() => {
                    this.project.status = this.project.status_prev;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatus(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            text: text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Research/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                }).subscribe(() => {
                    this.project.status_prev = this.project.status;
                    this.project.status = projectStatus;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatusWithInput(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            text: text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
            input: 'text',
            inputLabel: 'Begründung',
            inputValidator: (value) => {
                if (!value) {
                    return 'Bitte eine Begründung eingeben!'
                }
                return null;
            }
        }).then(result => {
            if (result.isConfirmed) {
                this.dataService.request('Research/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                    input: result.value,
                }).subscribe(() => {
                    this.project.status = projectStatus;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatusWithDoubleInput(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            html:
                '<p>' + text + '</p>' +
                '<label for="swal-input1" class="swal2-input-label">Verantwortliche Person</label>' +
                '<input id="swal-input1" class="swal2-input" style="width: calc(100% - 85px)">' +
                '<label for="swal-input2" class="swal2-input-label">Kommentar</label>' +
                '<input id="swal-input2" class="swal2-input" style="width: calc(100% - 85px)">',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
            preConfirm: () => {
                // @ts-ignore
                const responsiblePerson = document.getElementById('swal-input1').value;
                // @ts-ignore
                const comment = document.getElementById('swal-input2').value;

                if (!responsiblePerson || !comment) {
                    Swal.showValidationMessage(
                        `Bitte die verantwortliche Person setzen!`
                    )
                    return false;
                } else {
                    return [
                        responsiblePerson,
                        comment
                    ]
                }
            },
        }).then(result => {
            if (result.isConfirmed) {
                this.dataService.request('Research/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                    input: result.value[0],
                    input2: result.value[1] == '' ? null : result.value[1],
                }).subscribe(() => {
                    this.project.status = projectStatus;

                    this.router.navigate(['/research']);
                });
            }
        });
    }

    /* Mark project as filed for completion so COMPLETION tab shows up, but don't set status yet */
    fileCompletion(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Sicher, dass Sie die den Projektabschluss durchführen wollen?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
        }).then(result => {
            if (result.isConfirmed) {
                this.filedCompletion = true;
                this.setTab(this.ProjectTab.COMPLETION);
            }
        });
    }

    onePager() {
        this.exporting = true;
        this.dataService.request('Export/onePagerResearch', {
            PID: this.PID,
        }).subscribe(response => {
            this.exporting = false;
            const link = document.createElement('a');

            link.setAttribute('href', 'data:application/pdf;base64,' + response.export.data);
            link.setAttribute('download', response.export.filename);
            link.click();
        });
    }

    refresh() {
        this.loadProject();
        this.reloadChildren();
        this.toastrService.success('Daten wurden neu geladen')
    }
}
